/**
 * pdf-erase.js — remove text from a PDF page's content stream, keeping
 * everything else byte-for-byte.
 *
 * WHY THIS EXISTS. Painting a filled rectangle over a paragraph hides it from
 * the eye and from nobody else: the words are still in the content stream, so
 * Ctrl+F finds them, copy-paste yields them, and every extractor — including
 * ours — reads the ORIGINAL text out of the "translated" file. Measured on
 * tests/fixtures/pdf-editor/contract-ru.pdf: 4 of 10 paragraphs came back
 * verbatim from the output's text layer while the page looked perfectly
 * translated. That is the same defect class as the famous redaction failures,
 * arriving through the front door of a translator.
 *
 * WHY NOT RASTERISE. /redact-pdf answers the same problem by rendering the page
 * to pixels, which is right for redaction and wrong here: a translated document
 * that cannot be searched, selected, or read by a screen reader is a picture of
 * a translation. The text has to go without the page going with it.
 *
 * WHAT IT DOES. The page's content stream is tokenised, an interpreter tracks
 * the graphics and text matrices exactly as a viewer would, and every
 * text-showing operator whose pen lands inside a region is deleted along with
 * its operands. Vectors, images, colours, clipping and every other operator are
 * copied through untouched — the output differs from the input ONLY where words
 * were removed.
 *
 * THE ONE HARD RULE. `(a) Tj (b) Tj` positions the second run by the ADVANCE of
 * the first, and PDF has no operator that moves the pen without also moving the
 * line matrix — so a dropped run cannot be compensated for. Show operators with
 * no positioning operator between them are therefore treated as one chain, and
 * a chain is dropped only when ALL of it is inside a region. Otherwise the whole
 * chain stays. That trades a rare, counted leak for a guarantee that nothing
 * ever shifts and nothing visible is ever deleted.
 *
 * Exposed as window.PdfTextErase.
 */
(function (root) {
    'use strict';
    if (root.PdfTextErase) return;

    // ── matrices, in PDF's row-vector convention ─────────────────────────
    // p' = p × M × CTM, so `mul(a, b)` is "a then b".

    function mul(a, b) {
        return [
            a[0] * b[0] + a[1] * b[2],
            a[0] * b[1] + a[1] * b[3],
            a[2] * b[0] + a[3] * b[2],
            a[2] * b[1] + a[3] * b[3],
            a[4] * b[0] + a[5] * b[2] + b[4],
            a[4] * b[1] + a[5] * b[3] + b[5],
        ];
    }
    function apply(m, x, y) { return [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]]; }
    var IDENTITY = [1, 0, 0, 1, 0, 0];

    // ── tokenizer ────────────────────────────────────────────────────────
    // Byte-level and non-destructive: every token records its byte range so the
    // output can be assembled by copying spans of the ORIGINAL bytes. Strings
    // are never decoded — nothing here needs to know an encoding, and not
    // decoding them is why binary content survives intact.

    var WS = { 0: 1, 9: 1, 10: 1, 12: 1, 13: 1, 32: 1 };
    var DELIM = { 40: 1, 41: 1, 60: 1, 62: 1, 91: 1, 93: 1, 123: 1, 125: 1, 47: 1, 37: 1 };

    function tokenize(b) {
        var toks = [], i = 0, n = b.length;
        while (i < n) {
            var c = b[i];
            if (WS[c]) { i++; continue; }

            if (c === 37) {                                   // % comment
                while (i < n && b[i] !== 10 && b[i] !== 13) i++;
                continue;
            }

            var start = i;

            if (c === 40) {                                   // ( literal string )
                var depth = 0;
                while (i < n) {
                    var ch = b[i];
                    if (ch === 92) { i += 2; continue; }       // backslash escape
                    if (ch === 40) depth++;
                    else if (ch === 41) { depth--; if (!depth) { i++; break; } }
                    i++;
                }
                toks.push({ t: 'str', s: start, e: i });
                continue;
            }

            if (c === 60 && b[i + 1] === 60) {                // << dict >>
                toks.push({ t: 'dict_open', s: start, e: i + 2 }); i += 2; continue;
            }
            if (c === 62 && b[i + 1] === 62) {
                toks.push({ t: 'dict_close', s: start, e: i + 2 }); i += 2; continue;
            }
            if (c === 60) {                                   // < hex string >
                while (i < n && b[i] !== 62) i++;
                i++;
                toks.push({ t: 'str', s: start, e: i });
                continue;
            }
            if (c === 91) { toks.push({ t: 'arr_open', s: start, e: ++i }); continue; }
            if (c === 93) { toks.push({ t: 'arr_close', s: start, e: ++i }); continue; }

            if (c === 47) {                                   // /Name
                i++;
                while (i < n && !WS[b[i]] && !DELIM[b[i]]) i++;
                toks.push({ t: 'name', s: start, e: i });
                continue;
            }

            if ((c >= 48 && c <= 57) || c === 43 || c === 45 || c === 46) {
                i++;
                while (i < n && ((b[i] >= 48 && b[i] <= 57) || b[i] === 46 || b[i] === 45 || b[i] === 43)) i++;
                var num = 0;
                try { num = parseFloat(String.fromCharCode.apply(null, b.subarray(start, i))) || 0; }
                catch (e) { num = 0; }
                toks.push({ t: 'num', s: start, e: i, v: num });
                continue;
            }

            // operator
            i++;
            while (i < n && !WS[b[i]] && !DELIM[b[i]]) i++;
            var op = String.fromCharCode.apply(null, b.subarray(start, i));
            toks.push({ t: 'op', s: start, e: i, op: op });

            // Inline image: everything between ID and EI is raw bytes that must
            // not be tokenised. Skipping it wrong turns image data into
            // operators and the rewrite corrupts the page.
            if (op === 'ID') {
                var j = i + 1;
                while (j < n - 1) {
                    if (b[j] === 69 && b[j + 1] === 73 && WS[b[j - 1]] &&
                        (j + 2 >= n || WS[b[j + 2]] || DELIM[b[j + 2]])) break;
                    j++;
                }
                toks.push({ t: 'op', s: i, e: Math.min(n, j + 2), op: 'EI' });
                i = Math.min(n, j + 2);
            }
        }
        return toks;
    }

    // ── the interpreter ──────────────────────────────────────────────────

    var SHOW = { Tj: 1, TJ: 1, "'": 1, '"': 1 };
    var MOVES_PEN = { Td: 1, TD: 1, Tm: 1, 'T*': 1, BT: 1, ET: 1 };

    /**
     * Walk one content stream and decide, per show operator, whether its pen
     * lands inside a region.
     *
     * `hit(x, y)` is asked in DEVICE space — the same y-down space the layout
     * side of this feature already works in — so the caller never has to think
     * about user space, rotation or the CropBox.
     */
    function scan(bytes, toks, ctm0, vpTransform, hit, forms, out) {
        var ctm = ctm0.slice(), stack = [];
        var tm = IDENTITY.slice(), tlm = IDENTITY.slice();
        var leading = 0, rise = 0;
        var operands = [];
        var chain = [];            // consecutive show ops with no positioning between
        var results = [];

        function endChain() {
            if (!chain.length) return;
            var all = chain.every(function (c) { return c.inside; });
            chain.forEach(function (c) { c.drop = all; });
            chain = [];
        }

        for (var k = 0; k < toks.length; k++) {
            var t = toks[k];
            if (t.t !== 'op') { operands.push(t); continue; }
            var op = t.op;

            if (SHOW[op]) {
                // `'` and `"` move to the next line BEFORE showing.
                if (op === "'" || op === '"') {
                    tlm = mul([1, 0, 0, 1, 0, -leading], tlm);
                    tm = tlm.slice();
                    endChain();
                }
                var pt = apply(mul(tm, ctm), 0, rise);
                var dev = apply(vpTransform, pt[0], pt[1]);
                var rec = {
                    tokenIndex: k,
                    start: operands.length ? operands[0].s : t.s,
                    end: t.e,
                    op: op,
                    operands: operands.slice(),
                    inside: hit(dev[0], dev[1]),
                    drop: false,
                };
                results.push(rec);
                chain.push(rec);
                operands = [];
                continue;
            }

            if (MOVES_PEN[op]) endChain();

            switch (op) {
                case 'q': stack.push(ctm.slice()); break;
                case 'Q': if (stack.length) ctm = stack.pop(); break;
                case 'cm':
                    if (operands.length >= 6) {
                        var m = operands.slice(-6).map(function (o) { return o.v || 0; });
                        ctm = mul(m, ctm);
                    }
                    break;
                case 'BT': tm = IDENTITY.slice(); tlm = IDENTITY.slice(); break;
                case 'ET': break;
                case 'Tm':
                    if (operands.length >= 6) {
                        tm = operands.slice(-6).map(function (o) { return o.v || 0; });
                        tlm = tm.slice();
                    }
                    break;
                case 'Td':
                    if (operands.length >= 2) {
                        tlm = mul([1, 0, 0, 1, operands[operands.length - 2].v || 0,
                            operands[operands.length - 1].v || 0], tlm);
                        tm = tlm.slice();
                    }
                    break;
                case 'TD':
                    if (operands.length >= 2) {
                        leading = -(operands[operands.length - 1].v || 0);
                        tlm = mul([1, 0, 0, 1, operands[operands.length - 2].v || 0,
                            operands[operands.length - 1].v || 0], tlm);
                        tm = tlm.slice();
                    }
                    break;
                case 'T*':
                    tlm = mul([1, 0, 0, 1, 0, -leading], tlm);
                    tm = tlm.slice();
                    break;
                case 'TL': if (operands.length) leading = operands[operands.length - 1].v || 0; break;
                case 'Ts': if (operands.length) rise = operands[operands.length - 1].v || 0; break;
                case 'Do':
                    // Text can live in a Form XObject. A page whose words are
                    // all inside one is otherwise erased perfectly and changes
                    // nothing, which is the worst possible outcome: silent.
                    if (forms && operands.length) {
                        var nm = operands[operands.length - 1];
                        if (nm.t === 'name') {
                            forms.push({ name: nameOf(bytes, nm), ctm: ctm.slice() });
                        }
                    }
                    break;
                default: break;
            }
            operands = [];
        }
        endChain();
        out.push.apply(out, results);
        return results;
    }

    function nameOf(bytes, tok) {
        var s = '';
        for (var i = tok.s + 1; i < tok.e; i++) s += String.fromCharCode(bytes[i]);
        return s.replace(/#([0-9a-f]{2})/gi, function (_, h) { return String.fromCharCode(parseInt(h, 16)); });
    }

    // ── rewriting ────────────────────────────────────────────────────────

    var ENC = typeof TextEncoder !== 'undefined' ? new TextEncoder() : null;
    function bytesOf(s) {
        if (ENC) return ENC.encode(s);
        var a = new Uint8Array(s.length);
        for (var i = 0; i < s.length; i++) a[i] = s.charCodeAt(i) & 0xff;
        return a;
    }

    /**
     * Emit the stream with the dropped runs removed.
     *
     * `'` and `"` are not just show operators — they also advance a line, and
     * `"` sets word and character spacing. Deleting them outright loses that,
     * so they are REPLACED by the positioning they carried rather than removed.
     */
    function rewrite(bytes, drops) {
        if (!drops.length) return bytes;
        var parts = [], cursor = 0;
        drops.sort(function (a, b) { return a.start - b.start; });
        drops.forEach(function (d) {
            if (d.start < cursor) return;                     // overlapping — skip
            parts.push(bytes.subarray(cursor, d.start));
            if (d.op === "'") {
                parts.push(bytesOf(' T* '));
            } else if (d.op === '"') {
                var aw = d.operands.length >= 3 ? d.operands[d.operands.length - 3].v : 0;
                var ac = d.operands.length >= 2 ? d.operands[d.operands.length - 2].v : 0;
                parts.push(bytesOf(' ' + aw + ' Tw ' + ac + ' Tc T* '));
            } else {
                parts.push(bytesOf(' '));
            }
            cursor = d.end;
        });
        parts.push(bytes.subarray(cursor));
        var total = parts.reduce(function (n, p) { return n + p.length; }, 0);
        var out = new Uint8Array(total), off = 0;
        parts.forEach(function (p) { out.set(p, off); off += p.length; });
        return out;
    }

    // ── pdf-lib plumbing ─────────────────────────────────────────────────

    function streamBytes(PDFLib, obj) {
        try { return PDFLib.decodePDFRawStream(obj).decode(); }
        catch (e) { return null; }
    }

    function pageStreamRefs(PDFLib, page) {
        var ctx = page.doc.context;
        var contents = page.node.get(PDFLib.PDFName.of('Contents'));
        var refs = [];
        if (!contents) return refs;
        var resolved = contents instanceof PDFLib.PDFRef ? ctx.lookup(contents) : contents;
        if (resolved instanceof PDFLib.PDFArray) {
            for (var i = 0; i < resolved.size(); i++) {
                var r = resolved.get(i);
                refs.push({ ref: r, obj: ctx.lookup(r) });
            }
        } else if (contents instanceof PDFLib.PDFRef) {
            refs.push({ ref: contents, obj: resolved });
        } else {
            refs.push({ ref: null, obj: resolved });
        }
        return refs;
    }

    /**
     * erasePage(PDFLib, page, regions, vpTransform, opts) → stats
     *
     * `regions` are boxes {x,y,w,h} in device space (y down) at the same scale
     * the caller used to build `vpTransform`.
     */
    function erasePage(PDFLib, page, regions, vpTransform, opts) {
        opts = opts || {};
        var ctx = page.doc.context;
        var stats = { shown: 0, inside: 0, dropped: 0, chainKept: 0, forms: 0, sharedForms: 0 };
        if (!regions.length) return stats;

        // A baseline sits near the bottom of an ink box, and a descender can put
        // it a hair below; the tolerance is a fraction of the box so it holds at
        // any font size.
        function hit(x, y) {
            for (var i = 0; i < regions.length; i++) {
                var r = regions[i];
                var tolY = Math.max(1.5, r.h * 0.28);
                var tolX = Math.max(1.5, r.h * 0.35);
                if (x >= r.x - tolX && x <= r.x + r.w + tolX
                    && y >= r.y - tolY && y <= r.y + r.h + tolY) return true;
            }
            return false;
        }

        var seenForms = opts.seenForms || {};
        var resources = page.node.Resources();

        function process(bytes, ctm, res, depth) {
            var toks = tokenize(bytes);
            var forms = [];
            var results = [];
            scan(bytes, toks, ctm, vpTransform, hit, forms, results);

            results.forEach(function (r) {
                stats.shown++;
                if (r.inside) stats.inside++;
                if (r.drop) stats.dropped++;
                else if (r.inside) stats.chainKept++;
            });

            var out = rewrite(bytes, results.filter(function (r) { return r.drop; }));

            if (depth < 4 && forms.length && res) {
                var xdict = res.lookupMaybe
                    ? res.lookupMaybe(PDFLib.PDFName.of('XObject'), PDFLib.PDFDict)
                    : null;
                if (xdict) {
                    forms.forEach(function (f) {
                        var ref = xdict.get(PDFLib.PDFName.of(f.name));
                        if (!ref) return;
                        var obj = ctx.lookup(ref);
                        if (!obj || !obj.dict) return;
                        var sub = obj.dict.get(PDFLib.PDFName.of('Subtype'));
                        if (!sub || String(sub) !== '/Form') return;
                        var key = String(ref);
                        if (seenForms[key]) { stats.sharedForms++; return; }
                        seenForms[key] = 1;
                        var fb = streamBytes(PDFLib, obj);
                        if (!fb) return;
                        var mtx = obj.dict.get(PDFLib.PDFName.of('Matrix'));
                        var fm = IDENTITY;
                        if (mtx && mtx.size && mtx.size() === 6) {
                            fm = [];
                            for (var q = 0; q < 6; q++) fm.push(mtx.get(q).asNumber());
                        }
                        var fres = obj.dict.get(PDFLib.PDFName.of('Resources'));
                        if (fres instanceof PDFLib.PDFRef) fres = ctx.lookup(fres);
                        var rewritten = process(fb, mul(fm, f.ctm), fres || res, depth + 1);
                        if (rewritten !== fb) {
                            stats.forms++;
                            ctx.assign(ref, ctx.flateStream(rewritten, cloneDict(PDFLib, obj.dict)));
                        }
                    });
                }
            }
            return out;
        }

        var refs = pageStreamRefs(PDFLib, page);
        if (!refs.length) return stats;

        // Concatenate first: a paragraph can straddle two content streams, and
        // the graphics state carries across them.
        var joined = concat(refs.map(function (r) {
            return streamBytes(PDFLib, r.obj) || new Uint8Array(0);
        }));
        var result = process(joined, IDENTITY, resources, 0);

        var newRef = ctx.register(ctx.flateStream(result));
        page.node.set(PDFLib.PDFName.of('Contents'), newRef);
        return stats;
    }

    function cloneDict(PDFLib, dict) {
        var out = {};
        dict.keys().forEach(function (k) {
            var key = String(k).replace(/^\//, '');
            if (key === 'Length' || key === 'Filter' || key === 'DecodeParms') return;
            out[key] = dict.get(k);
        });
        return out;
    }

    function concat(list) {
        var sep = bytesOf('\n');
        var total = list.reduce(function (n, a) { return n + a.length + sep.length; }, 0);
        var out = new Uint8Array(total), off = 0;
        list.forEach(function (a) { out.set(a, off); off += a.length; out.set(sep, off); off += sep.length; });
        return out;
    }

    root.PdfTextErase = {
        erasePage: erasePage,
        // exposed for tests
        _tokenize: tokenize,
        _mul: mul,
        _apply: apply,
        _rewrite: rewrite,
    };
})(typeof self !== 'undefined' ? self : this);
